import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, User } from 'lucide-react';
import { createCustomer } from '../../../api/customer.api';
import FormattedInput from '../../../components/common/FormattedInput';
import styles from '../../../assets/scss/pages/CustomerCreate.module.scss';

export default function CustomerCreate() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ name: '', phone: '' });
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const capitalizeWords = (str) =>
        str.replace(/(^|\s)\S/g, (char) => char.toUpperCase());

    const formatPhoneInput = (digits) => {
        if (digits.length <= 4) return digits;
        if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
        return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    };

    const update = (field, val) => {
        setFormData((prev) => ({ ...prev, [field]: val }));
        if (error) setError('');
    };

    const parsePhone = (typed) => typed.replace(/\D/g, '').slice(0, 10);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const name = formData.name.trim();
        const phone = formData.phone.trim();

        if (!name) {
            setError('Vui lòng nhập tên khách hàng');
            return;
        }
        if (!phone) {
            setError('Vui lòng nhập số điện thoại');
            return;
        }
        if (!/^0\d{9}$/.test(phone)) {
            setError('Số điện thoại không hợp lệ (10 số, bắt đầu bằng 0)');
            return;
        }

        setSaving(true);
        try {
            await createCustomer({ name, phone });
            setShowSuccess(true);
            setTimeout(() => {
                navigate('/admin/customers', { replace: true });
            }, 1200);
        } catch (err) {
            const msg = err.response?.data?.message || 'Thêm khách hàng thất bại';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft />
                    </button>
                    <h1 className={styles.headerTitle}>Thêm khách hàng</h1>
                </div>
            </div>

            {/* Form */}
            <div className={styles.card}>
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Tên khách hàng</label>
                        <div className={styles.inputWrap}>
                            <span className={styles.inputIcon}><User /></span>
                            <FormattedInput
                                type="text"
                                className={styles.input}
                                placeholder="Nhập tên khách hàng"
                                value={formData.name}
                                format={capitalizeWords}
                                parse={capitalizeWords}
                                isValueChar={() => true}
                                onChange={(raw) => update('name', raw)}
                            />
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Số điện thoại</label>
                        <div className={styles.inputWrap}>
                            <span className={styles.inputIcon}><Phone /></span>
                            <FormattedInput
                                type="tel"
                                inputMode="numeric"
                                className={styles.input}
                                placeholder="Nhập số điện thoại (VD: 0912 345 678)"
                                value={formData.phone}
                                format={formatPhoneInput}
                                parse={parsePhone}
                                onChange={(raw) => update('phone', raw)}
                                maxLength={12}
                            />
                        </div>
                    </div>

                    {error && <div className={styles.error}>{error}</div>}

                    <button type="submit" className={styles.submitBtn} disabled={saving}>
                        {saving ? 'Đang tạo...' : 'Thêm khách hàng'}
                    </button>
                </form>
            </div>

            {/* Success Modal */}
            {showSuccess && (
                <div className={styles.successOverlay}>
                    <div className={styles.successModal}>
                        <div className={styles.successIcon}>✓</div>
                        <span className={styles.successText}>Thêm khách hàng thành công</span>
                    </div>
                </div>
            )}
        </div>
    );
}
